// Centralized path predicates. Lives separately from the page modules
// so App.js can decide which route is active WITHOUT eagerly importing
// the heavy page bundles those modules contain. React.lazy + Suspense
// in App.js then only fetches the chunk for the matched route.
//
// Each page module still exports its own predicate (kept for any
// external caller / e2e test that imports it). The bodies here should
// stay in sync — they're the source of truth for App.js's dispatch.

const stripTrailing = () =>
  (typeof window !== 'undefined' ? window.location.pathname : '').replace(/\/$/, '');

const exact = (...paths) => () => paths.includes(stripTrailing());

export const isLegalPath = exact(
  '/terms', '/privacy', '/dmca', '/minor-safety',
  '/paid-features', '/law-enforcement', '/account-deletion',
);

export const isExplainerPath = exact(
  '/how-trading-works', '/how-it-works',
  '/how-local-prices-work', '/local-prices', '/local-lcs',
);

export const isSignupPath = exact('/signup');
export const isAuthFlowPath = exact('/forgot-password', '/reset-password', '/verify-email');
export const isPricingPath = exact('/pricing');
export const isStatusPath = exact('/status');

export const isStoresPath = () =>
  typeof window !== 'undefined' && window.location.pathname === '/stores';

export const isShowFloorMarketingPath = () =>
  typeof window !== 'undefined' && window.location.pathname === '/show-floor';

export const isStolenRegistryPath = () =>
  typeof window !== 'undefined'
    && ['/stolen', '/stolen-registry'].includes(window.location.pathname);

export const isLookupPath = () =>
  typeof window !== 'undefined'
    && ['/lookup', '/verify'].includes(window.location.pathname);

export const isWalletBouncePath = () =>
  typeof window !== 'undefined'
    && ['/wallet/return', '/wallet/refresh'].includes(window.location.pathname);

export const isCardChainPath = () =>
  typeof window !== 'undefined'
    && /^\/card\/([0-9a-f-]{36})\/?$/i.test(window.location.pathname);

export const isShowFloorEventPath = () =>
  typeof window !== 'undefined'
    && /^\/show\/([a-z0-9-]+)\/?$/i.test(window.location.pathname);

export const isShowFloorUserPath = () =>
  typeof window !== 'undefined'
    && /^\/show-floor\/([a-z0-9_-]+)\/?$/i.test(window.location.pathname);

export const isMarketplacePath = () => {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p === '/marketplace' || p.startsWith('/marketplace/');
};

export const isHelpPath = () => {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname.replace(/\/$/, '');
  return p === '/help' || p.startsWith('/help/');
};

export const isAppOrPublicBinderPath = () => {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname;
  return p.startsWith('/app')
    || p === '/contests' || p.startsWith('/contests/')
    || /^\/u\/[^/]+\/binder\//.test(p);
};

export const isFeesPath = () => {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname.replace(/\/$/, '');
  return p === '/fees';
};
